import { useEffect, useRef, useState } from 'react';
import { deriveScenarioPreview, type ScenarioPreview, type SimulationScenario } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { useT } from '../../i18n/index.js';

/** Accent de marque (terracotta) pour les boîtes de cibles — cohérent avec l'app. */
const ACCENT = '#e0854f';
/** Rouge « hostile » pour les menaces aériennes (ADR-0023) — distinct de l'accent UI. */
const THREAT = '#e0574f';

/**
 * Aperçu du Récapitulatif de simulation (ADR-0021, ADR-0033).
 *
 * Deux couches : un **fond photoréaliste** (image de référence de l'environnement, servie depuis le
 * stockage objet) et un **canevas** qui dessine, par-dessus, les boîtes de cibles/menaces
 * (préfiguration des auto-labels) et les nappes de lumière qui modulent le moment et le ciel. Si
 * l'image manque ou échoue au chargement, le canevas **retombe sur l'aperçu schématique 2.5D**
 * (sol/ciel/décor peints) : l'aperçu ne casse jamais. Ce n'est jamais le rendu photoréaliste **du
 * scénario** — c'est un fond représentatif plus la composition dérivée.
 */
export function ScenarioPreview({
  scenario,
  refreshKey = 0,
}: {
  scenario: SimulationScenario;
  /** Incrémenter pour forcer la relecture de l'URL du fond (ex. après un changement de fond). */
  refreshKey?: number;
}) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  // `true` seulement une fois l'image chargée avec succès ; sinon le canevas peint le schématique.
  const [photoReady, setPhotoReady] = useState(false);

  // URL de l'image de référence de l'environnement. Rechargée quand l'environnement change.
  useEffect(() => {
    let active = true;
    setPhotoReady(false);
    setPhotoUrl(null);
    apiFetch<{ url: string }>(`/simulation/preview/${scenario.environment}`)
      .then((r) => {
        if (active) setPhotoUrl(r.url);
      })
      .catch(() => {
        // Pas d'URL → on reste sur le schématique.
        if (active) setPhotoUrl(null);
      });
    return () => {
      active = false;
    };
  }, [scenario.environment, refreshKey]);

  // Peinture du canevas : overlays seuls si la photo est prête, schématique complet sinon.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const preview = deriveScenarioPreview(scenario);

    const paint = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (photoReady) drawOverlays(ctx, preview, w, h);
      else drawSchematic(ctx, preview, w, h);
    };

    paint();
    const ro = new ResizeObserver(paint);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [scenario, photoReady]);

  return (
    <>
      {photoUrl && (
        <img
          className="ddp-sim__preview-photo"
          src={photoUrl}
          alt=""
          aria-hidden="true"
          data-ready={photoReady ? 'true' : 'false'}
          onLoad={() => setPhotoReady(true)}
          onError={() => setPhotoReady(false)}
        />
      )}
      <canvas ref={canvasRef} className="ddp-sim__preview-canvas" aria-hidden="true" />
      <span className="ddp-sim__preview-cap">
        {t(photoReady ? 'sim.recap.previewPhoto' : 'sim.recap.previewSchematic')}
      </span>
    </>
  );
}

/**
 * Couche par-dessus la photo : nappes de lumière (moment/ciel) puis boîtes. Le fond photo porte
 * déjà le référentiel neutre (jour, ciel dégagé) ; ces nappes le modulent sans nouvelle image.
 */
function drawOverlays(
  ctx: CanvasRenderingContext2D,
  p: ScenarioPreview,
  w: number,
  h: number,
): void {
  ctx.clearRect(0, 0, w, h);
  applyAtmosphere(ctx, p, w, h);
  drawTargetsAndThreats(ctx, p, w, h);
}

function drawSchematic(
  ctx: CanvasRenderingContext2D,
  p: ScenarioPreview,
  w: number,
  h: number,
): void {
  ctx.clearRect(0, 0, w, h);
  const horizon = p.horizonY * h;

  // Ciel (seulement s'il est visible sous ce cadrage).
  if (p.horizonY < 0.999) {
    const sky = ctx.createLinearGradient(0, 0, 0, horizon);
    sky.addColorStop(0, p.sky[0]);
    sky.addColorStop(1, p.sky[1]);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, horizon);

    // Soleil / lune : halo doux.
    if (p.sun.visible) {
      const sx = p.sun.x * w;
      const sy = (1 - p.sun.y) * horizon;
      const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, Math.max(w, h) * 0.28);
      glow.addColorStop(0, 'rgba(255,244,214,0.95)');
      glow.addColorStop(0.5, 'rgba(255,224,170,0.28)');
      glow.addColorStop(1, 'rgba(255,224,170,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, horizon);
    }
  }

  // Sol : dégradé subtil vers le lointain pour donner de la profondeur (2.5D).
  const ground = ctx.createLinearGradient(0, horizon, 0, h);
  ground.addColorStop(0, shade(p.ground, 0.82));
  ground.addColorStop(1, shade(p.ground, 1.08));
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon, w, h - horizon);

  // Décor : arbres (cercles) ou bâtiments (rectangles avec une ombre portée selon la dureté).
  for (const f of p.features) {
    const fx = f.x * w;
    const fy = f.y * h;
    const r = f.r * w;
    if (f.kind === 'tree') {
      ctx.fillStyle = 'rgba(20,40,20,0.55)';
      ctx.beginPath();
      ctx.arc(fx, fy, r, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const bw = r * 1.6;
      const bh = r * 2.4;
      if (p.shadowHardness > 0.05) {
        ctx.fillStyle = `rgba(0,0,0,${0.28 * p.shadowHardness})`;
        ctx.fillRect(fx - bw / 2 + r * 0.6, fy - bh / 2 + r * 0.4, bw, bh);
      }
      ctx.fillStyle = 'rgba(120,124,132,0.75)';
      ctx.fillRect(fx - bw / 2, fy - bh / 2, bw, bh);
    }
  }

  drawTargetsAndThreats(ctx, p, w, h);
  applyAtmosphere(ctx, p, w, h);
}

/** Cibles + menaces aériennes : boîtes englobantes, préfiguration des annotations générées. */
function drawTargetsAndThreats(
  ctx: CanvasRenderingContext2D,
  p: ScenarioPreview,
  w: number,
  h: number,
): void {
  ctx.lineWidth = 1.5;
  ctx.font = '9px "IBM Plex Sans Condensed", sans-serif';
  ctx.textBaseline = 'bottom';
  for (const target of p.targets) {
    const bw = target.w * w;
    const bh = target.h * w;
    const bx = target.x * w - bw / 2;
    const by = target.y * h - bh / 2;
    ctx.strokeStyle = ACCENT;
    ctx.strokeRect(bx, by, bw, bh);
    const label = target.label;
    const tw = ctx.measureText(label).width + 6;
    ctx.fillStyle = ACCENT;
    ctx.fillRect(bx, by - 12, tw, 12);
    ctx.fillStyle = '#161616';
    ctx.fillText(label, bx + 3, by - 1);
  }

  for (const th of p.threats) {
    const bw = th.w * w;
    const bh = th.h * w;
    const bx = th.x * w - bw / 2;
    const by = th.y * h - bh / 2;
    ctx.strokeStyle = THREAT;
    ctx.strokeRect(bx, by, bw, bh);
    // Petit repère de sens pour une trajectoire dirigée (piqué / passage).
    if (th.trajectory === 'DIVE' || th.trajectory === 'PASS') {
      const tipX = th.trajectory === 'DIVE' ? bx + bw + bw * 0.5 : bx + bw + bw * 0.6;
      const tipY = th.trajectory === 'DIVE' ? by + bh : by + bh / 2;
      ctx.beginPath();
      ctx.moveTo(bx + bw, by + bh / 2);
      ctx.lineTo(tipX, tipY);
      ctx.stroke();
    }
  }
}

/** Voile de brume/couvert (nappe claire) et assombrissement nuit/crépuscule (nappe sombre). */
function applyAtmosphere(
  ctx: CanvasRenderingContext2D,
  p: ScenarioPreview,
  w: number,
  h: number,
): void {
  if (p.haze > 0) {
    ctx.fillStyle = `rgba(206,214,224,${p.haze * 0.5})`;
    ctx.fillRect(0, 0, w, h);
  }
  if (p.ambient < 1) {
    ctx.fillStyle = `rgba(8,12,24,${(1 - p.ambient) * 0.55})`;
    ctx.fillRect(0, 0, w, h);
  }
}

/** Éclaircit (>1) ou assombrit (<1) une couleur hex, en restant borné `[0,255]`. */
function shade(hex: string, factor: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  const r = clamp(((n >> 16) & 0xff) * factor);
  const g = clamp(((n >> 8) & 0xff) * factor);
  const b = clamp((n & 0xff) * factor);
  return `rgb(${r},${g},${b})`;
}
